<template>
    <div class="pdf-viewer" ref="viewer">
        <!-- Toolbar -->
        <div class="pdf-toolbar d-flex align-center px-3" style="height: 40px; background: #333; color: #fff; gap: 8px;">
            <!-- Page navigation -->
            <v-btn icon x-small dark :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">
                <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="text-caption" style="min-width: 80px; text-align: center;">
                {{ currentPage }} / {{ totalPages || '—' }}
            </span>
            <v-btn icon x-small dark :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">
                <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>

            <v-divider vertical dark class="mx-1" style="height: 20px;" />

            <!-- Zoom -->
            <v-btn icon x-small dark @click="zoomOut">
                <v-icon small>mdi-minus</v-icon>
            </v-btn>
            <span class="text-caption" style="min-width: 48px; text-align: center;">{{ Math.round(scale * 100) }}%</span>
            <v-btn icon x-small dark @click="zoomIn">
                <v-icon small>mdi-plus</v-icon>
            </v-btn>
            <v-btn icon x-small dark @click="fitWidth" title="Fit width">
                <v-icon small>mdi-arrow-expand-horizontal</v-icon>
            </v-btn>

            <v-spacer />

            <!-- Download / Open in new tab -->
            <v-btn icon x-small dark :href="src" target="_blank" title="Open in new tab">
                <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="d-flex align-center justify-center" style="flex: 1;">
            <v-progress-circular indeterminate color="primary" />
        </div>

        <!-- Error -->
        <div v-else-if="error" class="d-flex flex-column align-center justify-center pa-6" style="flex: 1;">
            <v-icon size="48" color="error">mdi-file-alert-outline</v-icon>
            <p class="mt-3 text-body-2 grey--text text-center">{{ error }}</p>
            <v-btn small outlined class="mt-2" :href="src" target="_blank">Open in browser</v-btn>
        </div>

        <!-- Pages container -->
        <div v-else ref="pagesContainer" class="pdf-pages-container" @scroll="onScroll">
            <div v-for="page in renderedPages" :key="page.num"
                class="pdf-page"
                :ref="'page-' + page.num"
            >
                <canvas :ref="'canvas-' + page.num" />
            </div>
        </div>
    </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

export default {
    name: 'PdfViewer',
    props: {
        src: { type: String, default: '' },
        pdfData: { default: null },
    },
    data() {
        return {
            pdfDoc: null,
            totalPages: 0,
            currentPage: 1,
            scale: 1.0,
            loading: true,
            error: null,
            renderedPages: [],
            renderQueue: new Set(),
        };
    },
    watch: {
        src: {
            handler(val) {
                if (val) this.loadPdf();
            },
            immediate: false,
        },
        pdfData: {
            handler(val) {
                if (val) this.loadPdf();
            },
            immediate: false,
        },
        scale() {
            this.rerenderAll();
        },
    },
    mounted() {
        if (this.pdfData || this.src) this.loadPdf();
    },
    beforeDestroy() {
        if (this.pdfDoc) {
            this.pdfDoc.destroy();
            this.pdfDoc = null;
        }
    },
    methods: {
        async loadPdf() {
            this.loading = true;
            this.error = null;
            this.renderedPages = [];
            this.currentPage = 1;

            try {
                let data;
                if (this.pdfData) {
                    // Use pre-fetched binary data (ArrayBuffer or Uint8Array)
                    data = this.pdfData instanceof ArrayBuffer ? this.pdfData : this.pdfData.buffer || this.pdfData;
                } else {
                    // Fetch from URL
                    const response = await fetch(this.src);
                    if (!response.ok) throw new Error('Failed to fetch PDF: ' + response.status);
                    data = await response.arrayBuffer();
                }

                this.pdfDoc = await pdfjsLib.getDocument({ data }).promise;
                this.totalPages = this.pdfDoc.numPages;

                // Prepare page slots
                this.renderedPages = Array.from({ length: this.totalPages }, (_, i) => ({
                    num: i + 1,
                    rendered: false,
                }));

                // Wait for DOM update then fit + render
                await this.$nextTick();
                this.fitWidth();
            } catch (e) {
                console.error('PDF load error:', e);
                this.error = 'Could not load PDF. ' + (e.message || '');
            } finally {
                this.loading = false;
            }
        },

        async renderPage(pageNum) {
            if (!this.pdfDoc || this.renderQueue.has(pageNum)) return;
            this.renderQueue.add(pageNum);

            try {
                const page = await this.pdfDoc.getPage(pageNum);
                const viewport = page.getViewport({ scale: this.scale });

                const canvasRef = this.$refs['canvas-' + pageNum];
                const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
                if (!canvas) return;

                const dpr = window.devicePixelRatio || 1;
                canvas.width = Math.floor(viewport.width * dpr);
                canvas.height = Math.floor(viewport.height * dpr);
                canvas.style.width = viewport.width + 'px';
                canvas.style.height = viewport.height + 'px';

                const ctx = canvas.getContext('2d');
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                await page.render({ canvasContext: ctx, viewport }).promise;

                const slot = this.renderedPages.find(p => p.num === pageNum);
                if (slot) slot.rendered = true;
            } catch (e) {
                console.error('Render error for page', pageNum, e);
            } finally {
                this.renderQueue.delete(pageNum);
            }
        },

        async rerenderAll() {
            // Mark all as not rendered, then render visible
            this.renderedPages.forEach(p => (p.rendered = false));
            await this.$nextTick();
            this.renderVisiblePages();
        },

        renderVisiblePages() {
            const container = this.$refs.pagesContainer;
            if (!container) return;

            const containerTop = container.scrollTop;
            const containerBottom = containerTop + container.clientHeight;

            for (const page of this.renderedPages) {
                const el = this.$refs['page-' + page.num];
                const pageEl = Array.isArray(el) ? el[0] : el;
                if (!pageEl) continue;

                const top = pageEl.offsetTop;
                const bottom = top + pageEl.offsetHeight;

                // Render if in viewport or within 1 page above/below
                const buffer = container.clientHeight;
                if (bottom >= containerTop - buffer && top <= containerBottom + buffer) {
                    if (!page.rendered) this.renderPage(page.num);
                }
            }
        },

        onScroll() {
            this.renderVisiblePages();
            this.updateCurrentPage();
        },

        updateCurrentPage() {
            const container = this.$refs.pagesContainer;
            if (!container) return;
            const midpoint = container.scrollTop + container.clientHeight / 2;

            for (const page of this.renderedPages) {
                const el = this.$refs['page-' + page.num];
                const pageEl = Array.isArray(el) ? el[0] : el;
                if (!pageEl) continue;

                if (pageEl.offsetTop <= midpoint && pageEl.offsetTop + pageEl.offsetHeight >= midpoint) {
                    this.currentPage = page.num;
                    break;
                }
            }
        },

        goToPage(num) {
            if (num < 1 || num > this.totalPages) return;
            this.currentPage = num;
            const el = this.$refs['page-' + num];
            const pageEl = Array.isArray(el) ? el[0] : el;
            if (pageEl) {
                pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },

        zoomIn() {
            this.scale = Math.min(this.scale + 0.25, 3.0);
        },
        zoomOut() {
            this.scale = Math.max(this.scale - 0.25, 0.5);
        },

        fitWidth() {
            if (!this.pdfDoc) return;
            // Use first page to determine width ratio
            this.pdfDoc.getPage(1).then(page => {
                const unscaledViewport = page.getViewport({ scale: 1.0 });
                const container = this.$refs.pagesContainer;
                if (!container) return;
                const availableWidth = container.clientWidth - 24; // 12px padding each side
                this.scale = availableWidth / unscaledViewport.width;
            });
        },
    },
};
</script>

<style scoped>
.pdf-viewer {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #525659;
}
.pdf-pages-container {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}
.pdf-page {
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
.pdf-page canvas {
    display: block;
}
</style>
